import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { MapPin, Phone, Mail, Wrench, Calendar } from "lucide-react";

interface Shop {
    shop_id: number;
    shop_name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    email: string | null;
    chain: { chain_name: string; logo_url: string | null } | null;
}

export default function RepairShopDetailPage() {
    const { shopId } = useParams();
    const [shop, setShop] = useState<Shop | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API_URL}/repair-shops/${shopId}`, { withCredentials: true })
            .then((r) => setShop(r.data.shop ?? null))
            .catch(() => setShop(null))
            .finally(() => setLoading(false));
    }, [shopId]);

    if (loading) return <p className="p-8 text-center text-muted-foreground">Loading…</p>;
    if (!shop)   return <p className="p-8 text-center text-muted-foreground">Shop not found.</p>;

    return (
        <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
            <div className="bg-card rounded-2xl border p-6 space-y-4">
                {shop.chain && (
                    <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
                        {shop.chain.chain_name}
                    </p>
                )}
                <div className="flex items-center gap-3">
                    <Wrench className="h-8 w-8 text-indigo-600" />
                    <h1 className="text-2xl font-bold">{shop.shop_name}</h1>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
                    {(shop.address || shop.city) && (
                        <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 shrink-0" />
                            {[shop.address, shop.city, shop.state, shop.zip].filter(Boolean).join(", ")}
                        </p>
                    )}
                    {shop.phone && (
                        <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0" />
                            {shop.phone}
                        </p>
                    )}
                    {shop.email && (
                        <p className="flex items-center gap-2">
                            <Mail className="h-4 w-4 shrink-0" />
                            {shop.email}
                        </p>
                    )}
                </div>
            </div>

            <div className="bg-card rounded-2xl border p-6 text-center space-y-3">
                <Calendar className="h-10 w-10 mx-auto text-indigo-600" />
                <h2 className="text-lg font-semibold">Ready to schedule service?</h2>
                <p className="text-sm text-muted-foreground">
                    Book online in minutes — choose your service, date, and time.
                </p>
                <Link
                    to={`/repair-shops/${shopId}/book`}
                    className="inline-block rounded-lg bg-indigo-600 px-6 py-2.5 text-sm text-white font-medium hover:bg-indigo-700 transition"
                >
                    Book an Appointment
                </Link>
            </div>
        </div>
    );
}
